import '../../styles/common/wrapper.scss';
import { useRef, useEffect, useState } from 'react';
import * as PIXI from 'pixi.js';
import Color from 'color';
import { DotFilter } from '../../filters/dot/DotFilter';
import Ripple from '../../filters/dot/ripple';

type Props = {
  bgColor?: string;
  dotColor?: string;
  height: number;
};

export default function App(props: Props) {
  const isPc = document.body.clientWidth >= 768;
  const amount = isPc ? 15 : 5;
  const boxSize = isPc ? 20 : 10;

  const bgRef = useRef<HTMLDivElement>(null);
  // filter は PIXI resize イベント時に uniform を更新するため ref で保持する。
  // これをせずに毎レンダー新規生成すると、shader 内の width/height が初期値のまま固定され、
  // キャンバスがリサイズしたときに縦横比が崩れる（ドットが楕円化する）。
  const filterRef = useRef<DotFilter | null>(null);
  // PIXI Application は resizeTo: HTMLElement 指定でも window.resize しか監視しない仕様のため、
  // 親コンポーネントで height 変化が発生した際は明示的に app.resize() を呼んで再計測させる必要がある。
  const appRef = useRef<PIXI.Application | null>(null);
  const [loaded, setLoaded] = useState<boolean>(false);
  const bgColor: Color = props.bgColor ? Color(props.bgColor) : Color.rgb(16, 35, 52);
  const dotColor: Color = props.dotColor ? Color(props.dotColor) : Color('#152F45');

  const init = () => {
    if (bgRef.current === null) return;
    const app = new PIXI.Application({
      backgroundColor: Number(bgColor.rgbNumber()),
      resizeTo: bgRef.current as HTMLElement,
    });
    appRef.current = app;
    const bg = new PIXI.Graphics();
    const container1 = new PIXI.Container();
    const container2 = new PIXI.Container();
    const blurFilter = new PIXI.filters.BlurFilter();
    blurFilter.blur = 50;

    const myFilter = new DotFilter({
      width: app.screen.width,
      height: app.screen.height,
      r: dotColor.red() / 255,
      g: dotColor.green() / 255,
      b: dotColor.blue() / 255,
      box: boxSize,
    });
    filterRef.current = myFilter;

    container1.filters = [blurFilter, myFilter];

    app.stage.interactive = true;
    app.stage.addChild(container1);
    app.stage.addChild(container2);

    container1.addChild(bg);
    const ripples = new Array(amount)
      .fill(null)
      .map(() => new Ripple(app.screen.width, app.screen.height));
    ripples.forEach((r) => {
      container1.addChild(r.ripple);
      container2.addChild(r.bone);
    });

    // resizeTo でキャンバス自体は自動リサイズされるが、DotFilter の width/height uniform も
    // 追従させないと shader 内の座標計算がズレて縦横比が壊れる。
    app.renderer.on('resize', (width: number, height: number) => {
      if (filterRef.current) {
        filterRef.current.uniforms.width = width;
        filterRef.current.uniforms.height = height;
      }
    });

    app.ticker.maxFPS = 10;
    app.ticker.add(() => {
      bg.clear();
      bg.beginFill(0xffffff);
      bg.drawRect(0, 0, app.screen.width, app.screen.height);
      bg.endFill();

      ripples.forEach((r) => {
        r.update(app.screen.width, app.screen.height, false);
      });
    });

    bgRef.current?.appendChild(app.view);
  };

  // props.height の変化（ローディング完了時など）で PIXI に再計測させる。
  // PIXI の resizeTo は window.resize しか購読しないため、要素サイズだけが変わった場合は
  // app.resize() を明示的に呼ばないとキャンバス実体が古いサイズのままになり、
  // shader の uniform 座標系と噛み合わずアスペクト比が崩れる。
  // app.resize() 内で 'resize' イベントが発火し、filter uniform も自動追従する。
  useEffect(() => {
    if (!appRef.current) return;
    if (props.height === 0) return;
    appRef.current.resize();
  }, [props.height]);

  //initialize
  useEffect(() => {
    if (!bgRef) return;
    if (props.height === 0) return;

    if (!loaded) {
      init();
      setLoaded(true);
    }
  }, [bgRef, props.height]);

  return <div className="bg" ref={bgRef} style={{ height: props.height }} />;
}
